import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  Stack,
  IconButton,
} from '@mui/material';
import { LocalOffer, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useCurrency } from '../context/CurrencyContext';

const ProductCard = ({ product }) => {
  const navigate = useNavigate();
  const [showFullDescription, setShowFullDescription] = useState(false);
  const { isIndia } = useCurrency();

  const handleCardClick = () => {
    navigate(`/products/${product.slug || product.id}`);
  };

  // Truncate description to 100 characters
  const truncatedDescription = product.description?.length > 100 
    ? product.description.substring(0, 100) + '...' 
    : product.description;

  // Get the lowest price variant
  const getLowestPriceVariant = () => {
    if (!product.variants || product.variants.length === 0) {
      return { sellingPrice: product.price || 0, mrp: product.mrp || 0 };
    }
    return product.variants.reduce((lowest, variant) => 
      variant.sellingPrice < lowest.sellingPrice ? variant : lowest
    , product.variants[0]);
  };

  const lowestVariant = getLowestPriceVariant();

  return (
    <Card
      onClick={handleCardClick}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 12px 32px rgba(201, 169, 97, 0.25)',
        },
      }}
    >
      <Box sx={{ position: 'relative' }}>
        <CardMedia
          component="img"
          height="240"
          image={product.image}
          alt={product.name}
          sx={{ objectFit: 'cover' }}
        />
        {product.featured && (
          <Chip
            icon={<LocalOffer />}
            label="Featured"
            color="primary"
            size="small"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              fontWeight: 600,
            }}
          />
        )}
      </Box>

      <CardContent sx={{ flexGrow: 1 }}>
        <Typography
          variant="overline"
          sx={{
            color: 'primary.main',
            fontWeight: 600,
            fontSize: '0.7rem',
          }}
        >
          {product.category}
        </Typography>

        <Typography
          variant="h6"
          className='font15'
          sx={{
            fontFamily: "'Cormorant Garamond', serif",
            fontWeight: 600,
            mb: 1,
            fontSize: '1.50rem',
          }}
        >
          {product.name}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2, lineHeight: 1.6 }}
        >
          {showFullDescription ? product.description : truncatedDescription}
          {product.description?.length > 100 && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setShowFullDescription(!showFullDescription);
              }}
              sx={{ 
                ml: 1, 
                p: 0.5,
                '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
              }}
            >
              {showFullDescription ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          )}
        </Typography>

        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
          {product.benefits.slice(0, 2).map((benefit, index) => (
            <Chip
              key={index}
              label={benefit}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 24,
                bgcolor: 'primary.main',
                color: 'white',
                opacity: 0.8,
              }}
            />
          ))}
        </Stack>

        
      </CardContent>

      <CardActions sx={{ p: 2, pt: 0 }}>
        {/* Price Display - Show only lowest variant */}
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 900,
                color: 'primary.main',
                fontSize: '2rem',
              }}
            >
              {lowestVariant.currencySymbol}{lowestVariant.sellingPrice.toFixed(2)}
            </Typography>
            {lowestVariant.mrp && lowestVariant.mrp > lowestVariant.sellingPrice && (
              <Typography
                variant="body2"
                sx={{
                  textDecoration: 'line-through',
                  color: 'text.secondary',
                  fontSize: '0.9rem',
                }}
              >
                {lowestVariant.currencySymbol}{lowestVariant.mrp.toFixed(2)}
              </Typography>
            )}
            {/* {lowestVariant.mrp && lowestVariant.mrp > lowestVariant.sellingPrice && (
              <Chip
                label={`${Math.round(((lowestVariant.mrp - lowestVariant.sellingPrice) / lowestVariant.mrp) * 100)}% OFF`}
                size="small"
                sx={{
                  fontSize: '0.6rem',
                  height: 20,
                  bgcolor: '#4caf50',
                  color: 'white',
                  fontWeight: 600,
                }}
              />
            )} */}
          </Stack>
          {product.variants && product.variants.length > 1 && (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', fontSize: '0.75rem' }}
            >
              Starting from {product.variants.length} variants
              {!isIndia && ' (USD)'}
            </Typography>
          )}
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            width: '100%',
            borderTop: '1px solid',
            borderColor: 'divider',
            pt: 2,
          }}
        >
          <Button
            variant="contained"
            className='view-btn'
            onClick={handleCardClick}
            sx={{
              borderRadius: 50,
              px: 4,
              py: 1,
              textTransform: 'none',
              fontWeight: 600,
              background: 'linear-gradient(135deg, #C9A961 0%, #B89850 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #B89850 0%, #A88740 100%)',
                transform: 'scale(1.05)',
              },
            }}
          >
            Add To Cart
          </Button>
        </Box>
      </CardActions>
    </Card>
  );
};

export default ProductCard;
